/**
 * KanbanBoard - Container principal do Kanban com drag & drop
 * 
 * Gerencia o layout das colunas e a movimentação de cards entre fases
 * 
 * v2.0.0 - Refatorado para arquitetura de paginação server-side por coluna
 */

import { useState, useMemo, useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  closestCenter,
  pointerWithin,
  rectIntersection,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
  type CollisionDetection,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  arrayMove
} from '@dnd-kit/sortable';
import { Plus, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { ScrollIndicators } from '@/components/ui/scroll-indicators';
import { KanbanColumn } from './KanbanColumn';
import { KanbanCard } from './KanbanCard';
import { ChatConversationModal } from './ChatConversationModal';
import { DealDetailsModalPremium } from '@/components/crm/deals/modal/DealDetailsModalPremium';
import { cn } from '@/lib/utils';
import { useKanbanConversationPhaseStats } from '@/hooks/useKanbanConversationPhaseStats';
import { useInvalidateKanbanConversationColumns } from '@/hooks/useKanbanConversationColumn';
import { useKanbanConversationRealtime } from '@/hooks/useKanbanConversationRealtime';
import { useReorderKanbanPhases } from '@/hooks/useReorderKanbanPhases';
import type { KanbanPhase, KanbanConversation } from '@/hooks/useKanban';
import type { KanbanConversationFilters } from '@/hooks/useKanbanConversationColumn';

/**
 * Converte cor hex para rgba com opacidade
 */
const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Paleta de 20 cores organizadas em 4 linhas temáticas
const PHASE_COLORS = [
  // Linha 1 - Cores primárias vibrantes
  '#3b82f6', // Azul
  '#22c55e', // Verde
  '#ef4444', // Vermelho
  '#f97316', // Laranja
  '#eab308', // Amarelo
  
  // Linha 2 - Tons alternativos
  '#8b5cf6', // Roxo
  '#ec4899', // Rosa
  '#06b6d4', // Ciano
  '#14b8a6', // Teal/Turquesa
  '#84cc16', // Lima/Verde limão
  
  // Linha 3 - Tons pastel/suaves
  '#60a5fa', // Azul claro
  '#4ade80', // Verde claro
  '#f87171', // Vermelho claro
  '#fb923c', // Laranja claro
  '#facc15', // Amarelo claro
  
  // Linha 4 - Tons neutros e escuros
  '#a855f7', // Violeta
  '#f472b6', // Rosa claro
  '#6b7280', // Cinza
  '#374151', // Cinza escuro
  '#1f2937', // Grafite
];

interface KanbanBoardProps {
  phases: KanbanPhase[];
  categoryId: string;
  filters?: KanbanConversationFilters;
  /** Exibe dados CRM (deal + contrato) nos cards */
  showCrmInfo?: boolean;
  onCreatePhase: (categoryId: string, name: string, color?: string) => Promise<any>;
  onUpdatePhase: (id: string, updates: Partial<KanbanPhase>) => Promise<any>;
  onDeletePhase: (id: string) => Promise<boolean>;
  onMoveConversation: (conversationId: string, newPhaseId: string) => Promise<boolean>;
  onRefresh?: () => void;
  // v2.1.0: Permissões granulares do menu 'kanban' para ocultar ações estruturais
  canCreatePhase?: boolean;
  canEditPhase?: boolean;
  canDeletePhase?: boolean;
  // Props de automação (opcionais)
  canViewAutomations?: boolean;
  hasAutomation?: (phaseId: string) => boolean;
  automationCount?: (phaseId: string) => number;
  onAutomationClick?: () => void;
}

export function KanbanBoard({
  phases,
  categoryId,
  filters,
  showCrmInfo = false,
  onCreatePhase,
  onUpdatePhase,
  onDeletePhase,
  onMoveConversation,
  onRefresh,
  // Permissões granulares (default true para retrocompatibilidade quando não informado)
  canCreatePhase = true,
  canEditPhase = true,
  canDeletePhase = true,
  // Props de automação
  canViewAutomations,
  hasAutomation,
  automationCount,
  onAutomationClick,
}: KanbanBoardProps) {
  // Estados para drag & drop e criação de fases
  const [activeConversation, setActiveConversation] = useState<KanbanConversation | null>(null);
  const [activeColumnId, setActiveColumnId] = useState<string | null>(null);
  const [isAddingPhase, setIsAddingPhase] = useState(false);
  const [isCreatingPhase, setIsCreatingPhase] = useState(false);
  const [newPhaseName, setNewPhaseName] = useState('');
  const [newPhaseColor, setNewPhaseColor] = useState(PHASE_COLORS[0]);
  
  // Estados para modal de chat
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);

  // Estados para modal de deal (CRM)
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  const [isDealModalOpen, setIsDealModalOpen] = useState(false);

  // Hook para stats das fases (contadores para badges)
  const { phases: phaseStats, statsMap, invalidateStats } = useKanbanConversationPhaseStats(categoryId, filters);

  // Realtime UNIFICADO do board (substitui as 2×N subscriptions por coluna)
  // Aplica patch otimista in-cache e coalesce rajadas via debounce.
  useKanbanConversationRealtime(categoryId);

  // Hook para invalidar colunas após mover conversa
  const invalidateAllColumns = useInvalidateKanbanConversationColumns();


  // Hook para reordenar colunas/fases - com callback de refresh para sincronizar estado local
  const reorderPhases = useReorderKanbanPhases(categoryId, onRefresh);

  // Estado local para ordenação visual durante arraste (optimistic)
  const [localPhaseOrder, setLocalPhaseOrder] = useState<KanbanPhase[] | null>(null);
  
  // Fases renderizadas: usa ordem local durante arraste, senão usa props
  const displayPhases = localPhaseOrder ?? phases;

  // IDs das colunas para SortableContext (prefixados para evitar conflito com IDs de conversa)
  const columnIds = useMemo(() => 
    displayPhases.map(p => `column-${p.id}`), 
    [displayPhases]
  );

  // Sensores para drag & drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Precisa arrastar 8px para ativar
      },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  /**
   * Collision detection customizada — ramifica conforme o tipo do item arrastado.
   *
   * Por que customizar: o DndContext registra TODOS os droppables da árvore
   * (sortables de coluna `column-<id>`, droppable interno de cada coluna com
   * `id = phase.id`, e cada conversa como sortable). Usando `closestCorners`
   * global, ao arrastar uma COLUNA o `over.id` quase sempre cai num card ou
   * no droppable interno de outra coluna — nunca num `column-<id>` — quebrando
   * o `arrayMove` e impedindo o preview/espaço reservado de aparecer.
   *
   * Solução cirúrgica:
   *  - Drag de COLUNA: restringe candidatos apenas aos sortables `column-*`
   *    e usa `closestCenter` (combina com `horizontalListSortingStrategy`).
   *  - Drag de CONVERSA: mantém o comportamento atual (`closestCorners` global),
   *    preservando a lógica de cards já testada.
   */
  const collisionDetectionStrategy: CollisionDetection = useCallback((args) => {
    const activeType = args.active.data.current?.type;

    if (activeType === 'column') {
      // Filtra apenas droppables que representam colunas (prefixo `column-`)
      const columnContainers = args.droppableContainers.filter((c) =>
        typeof c.id === 'string' && c.id.startsWith('column-'),
      );
      // Primeiro tenta interseção retangular (mais precisa em listas horizontais),
      // caindo para closestCenter quando o ponteiro está entre colunas.
      const intersections = rectIntersection({ ...args, droppableContainers: columnContainers });
      if (intersections.length > 0) return intersections;
      return closestCenter({ ...args, droppableContainers: columnContainers });
    }

    // Conversas: pointerWithin é mais preciso para soltar dentro da área de cards,
    // com fallback para closestCorners (comportamento original) quando o ponteiro
    // está sobre gaps entre cards/colunas.
    const pointerHits = pointerWithin(args);
    if (pointerHits.length > 0) return pointerHits;
    return closestCorners(args);
  }, []);

  // Início do arraste - detecta se é coluna ou card
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    const activeData = active.data.current;

    // Verifica se é reordenação de coluna
    if (activeData?.type === 'column') {
      setActiveColumnId(activeData.phaseId);
      setActiveConversation(null);
      // Inicializa ordem local para reordenação visual em tempo real
      setLocalPhaseOrder([...phases]);
      return;
    }

    // É uma conversa
    const conversationData = activeData?.conversation;
    if (conversationData) {
      setActiveColumnId(null);
      setActiveConversation(conversationData);
    }
  }, [phases]);

  // Durante o arraste - reordena visualmente as colunas em tempo real
  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { active, over } = event;
    if (!over || !localPhaseOrder) return;

    const activeData = active.data.current;
    // Só reordena visualmente se estamos arrastando uma coluna
    if (activeData?.type !== 'column') return;

    const activePhaseId = activeData.phaseId;
    const overData = over.data.current;
    const overIdStr = String(over.id);
    // Aceita tanto `column-<id>` (sortable da coluna) quanto `phase.id` cru
    // (droppable interno) — torna a detecção tolerante a qualquer alvo válido.
    const overPhaseId =
      overData?.phaseId ||
      overData?.phase?.id ||
      (overIdStr.startsWith('column-') ? overIdStr.replace('column-', '') : overIdStr);

    if (!overPhaseId || activePhaseId === overPhaseId) return;

    const oldIndex = localPhaseOrder.findIndex(p => p.id === activePhaseId);
    const newIndex = localPhaseOrder.findIndex(p => p.id === overPhaseId);

    // Ignora silenciosamente alvos que não são colunas conhecidas
    if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;

    // Atualiza estado visual local - as colunas deslizam em tempo real
    setLocalPhaseOrder(arrayMove(localPhaseOrder, oldIndex, newIndex));
  }, [localPhaseOrder]);

  // Fim do arraste - persiste reordenação ou movimento de conversa
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    const activeData = active.data.current;
    const overData = over?.data.current;

    // Captura a ordem visual final antes de limpar
    const finalOrder = localPhaseOrder;

    // Limpa estados de arraste
    setActiveConversation(null);
    setActiveColumnId(null);
    setLocalPhaseOrder(null);

    if (!over) return;

    const overId = over.id as string;

    // ==========================================================================
    // REORDENAÇÃO DE COLUNAS - Persiste a ordem visual final
    // ==========================================================================
    if (activeData?.type === 'column' && finalOrder) {
      const activePhaseId = activeData.phaseId;

      // Fallback: se o usuário soltou antes do onDragOver disparar,
      // tenta resolver o alvo no momento do end a partir do `over` atual.
      let effectiveOrder = finalOrder;
      const finalIndexBeforeFallback = finalOrder.findIndex(p => p.id === activePhaseId);
      const originalIndex = phases.findIndex(p => p.id === activePhaseId);

      if (finalIndexBeforeFallback === originalIndex) {
        const overIdStr = String(overId);
        const targetPhaseId =
          overData?.phaseId ||
          overData?.phase?.id ||
          (overIdStr.startsWith('column-') ? overIdStr.replace('column-', '') : overIdStr);
        const overIndex = finalOrder.findIndex(p => p.id === targetPhaseId);
        if (overIndex !== -1 && overIndex !== originalIndex) {
          effectiveOrder = arrayMove(finalOrder, originalIndex, overIndex);
        }
      }

      const finalIndex = effectiveOrder.findIndex(p => p.id === activePhaseId);

      if (originalIndex !== finalIndex) {
        // Usa a ordem visual final (já reordenada pelo onDragOver ou pelo fallback)
        const phaseOrders = effectiveOrder.map((p, index) => ({
          phase_id: p.id,
          sort_order: index
        }));

        // Executa mutation com optimistic update
        reorderPhases.mutate(phaseOrders);
      }
      return;
    }

    // ==========================================================================
    // MOVIMENTO DE CONVERSAS ENTRE COLUNAS
    // Resolução robusta do alvo (espelha o padrão do DealKanbanBoard):
    //   1) Soltou sobre outro card  → usa a fase desse card
    //   2) Soltou no droppable interno da coluna (área dos cards) → usa phase.id
    //   3) Soltou no sortable de reordenação da coluna (header/borda) → 'column-<id>'
    //   4) Fallback: overId é diretamente o phase.id
    // ==========================================================================
    const conversationId = active.id as string;
    const sourcePhaseId: string | null = activeData?.conversation?.kanban_phase_id || null;

    let targetPhaseId: string | null = null;

    if (overData?.type === 'conversation' && overData?.conversation?.kanban_phase_id) {
      targetPhaseId = overData.conversation.kanban_phase_id;
    } else if (overData?.type === 'phase' && overData?.phase?.id) {
      targetPhaseId = overData.phase.id;
    } else if (typeof overId === 'string' && overId.startsWith('column-')) {
      targetPhaseId = overId.replace('column-', '');
    } else {
      const directPhase = phases.find(p => p.id === overId);
      if (directPhase) targetPhaseId = directPhase.id;
    }

    // Se encontrou uma fase de destino válida e diferente da origem, move a conversa
    if (targetPhaseId && targetPhaseId !== sourcePhaseId) {
      const success = await onMoveConversation(conversationId, targetPhaseId);
      if (success) {
        // Invalida todas as colunas e stats para atualizar contadores
        invalidateAllColumns();
        invalidateStats();
      }
    }
  };

  // Cria nova fase — só fecha o formulário em caso de sucesso
  const handleCreatePhase = async () => {
    if (!newPhaseName.trim() || isCreatingPhase) return;

    // Proteção defensiva: o backend já bloqueia, mas evitamos a chamada
    // (e o erro silencioso) quando a UI sabe que não há permissão.
    if (!canCreatePhase) {
      toast.error('Você não tem permissão para criar fases do Kanban.');
      return;
    }

    try {
      setIsCreatingPhase(true);
      const created = await onCreatePhase(categoryId, newPhaseName.trim(), newPhaseColor);
      // Só limpa/fecha se a RPC retornou objeto (sucesso). Em erro, mantém o
      // formulário aberto para o usuário corrigir e ver o toast já emitido.
      if (created) {
        setNewPhaseName('');
        setNewPhaseColor(PHASE_COLORS[0]);
        setIsAddingPhase(false);
        invalidateStats();
      }
    } finally {
      setIsCreatingPhase(false);
    }
  };

  // Abre modal de chat ao clicar na conversa
  const handleConversationClick = (conversation: KanbanConversation) => {
    setSelectedConversationId(conversation.id);
    setIsChatModalOpen(true);
  };

  /** Abre modal de deal ao clicar na seção CRM do card */
  const handleCrmClick = (dealId: string) => {
    setSelectedDealId(dealId);
    setIsDealModalOpen(true);
  };
  
  // Fecha modal de chat e atualiza board
  const handleCloseModal = (open: boolean) => {
    setIsChatModalOpen(open);
    if (!open) {
      setSelectedConversationId(null);
      invalidateAllColumns();
      invalidateStats();
      onRefresh?.();
    }
  };

  /** Fecha modal de deal e atualiza board */
  const handleCloseDealModal = (open: boolean) => {
    setIsDealModalOpen(open);
    if (!open) {
      setSelectedDealId(null);
      // Invalida dados CRM nos cards para refletir mudanças
      invalidateAllColumns();
      invalidateStats();
      onRefresh?.();
    }
  };

  // Callback para quando conversas são adicionadas
  const handleConversationsAdded = () => {
    invalidateStats();
    onRefresh?.();
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetectionStrategy}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <ScrollIndicators className="w-full">
        <ScrollArea className="w-full">
          <div className="flex gap-4 p-4 h-[calc(100vh-200px)]">
            {/* SortableContext para reordenação de colunas via drag & drop */}
            <SortableContext items={columnIds} strategy={horizontalListSortingStrategy}>
              {/* Colunas de Fases - usa displayPhases para animação em tempo real */}
              {displayPhases.map((phase) => (
                <KanbanColumn
                  key={phase.id}
                  phase={phase}
                  filters={filters}
                  conversationCount={statsMap.get(phase.id)}
                  showCrmInfo={showCrmInfo}
                  onUpdatePhase={onUpdatePhase}
                  onDeletePhase={onDeletePhase}
                  onConversationClick={handleConversationClick}
                  onCrmClick={handleCrmClick}
                  onConversationsAdded={handleConversationsAdded}
                  // Permissões granulares (oculta ações estruturais)
                  canEditPhase={canEditPhase}
                  canDeletePhase={canDeletePhase}
                  // Props de automação
                  hasAutomation={canViewAutomations && hasAutomation?.(phase.id)}
                  automationCount={canViewAutomations ? automationCount?.(phase.id) || 0 : 0}
                  onAutomationClick={canViewAutomations ? onAutomationClick : undefined}
                />
              ))}
            </SortableContext>

            {/* Botão/Input para Adicionar Nova Fase — apenas para quem tem permissão */}
            {canCreatePhase && (
              <div className="w-72 shrink-0">
                {isAddingPhase ? (
                  <div className="p-3 rounded-lg border border-dashed border-border bg-muted/20">
                    <Input
                      placeholder="Nome da fase..."
                      value={newPhaseName}
                      onChange={(e) => setNewPhaseName(e.target.value)}
                      className="mb-3"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleCreatePhase();
                        if (e.key === 'Escape') {
                          setIsAddingPhase(false);
                          setNewPhaseName('');
                          setNewPhaseColor(PHASE_COLORS[0]);
                        }
                      }}
                    />

                    {/* Seletor de Cor */}
                    <div className="mb-3">
                      <span className="text-xs text-muted-foreground mb-1.5 block">Cor:</span>
                      <div className="flex flex-wrap gap-1.5">
                        {PHASE_COLORS.map((color) => (
                          <button
                            key={color}
                            type="button"
                            className={cn(
                              'w-6 h-6 rounded-full border-2 transition-all flex items-center justify-center',
                              newPhaseColor === color
                                ? 'border-foreground scale-110'
                                : 'border-transparent hover:scale-105'
                            )}
                            style={{ backgroundColor: color }}
                            onClick={() => setNewPhaseColor(color)}
                          >
                            {newPhaseColor === color && (
                              <Check className="h-3.5 w-3.5 text-white" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleCreatePhase} disabled={isCreatingPhase || !newPhaseName.trim()}>
                        {isCreatingPhase ? 'Salvando…' : 'Adicionar'}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setIsAddingPhase(false);
                          setNewPhaseName('');
                          setNewPhaseColor(PHASE_COLORS[0]);
                        }}
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full h-12 border-dashed"
                    onClick={() => setIsAddingPhase(true)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Nova Fase
                  </Button>
                )}
              </div>
            )}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </ScrollIndicators>

      {/* Overlay durante arraste - preview realista */}
      <DragOverlay
        dropAnimation={{
          duration: 250,
          easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
        }}
      >
        {activeConversation && (
          <div className="rotate-3 scale-105">
            <KanbanCard conversation={activeConversation} />
          </div>
        )}
        {activeColumnId && (() => {
          // Renderiza preview realista da coluna arrastada
          const draggedPhase = phases.find(p => p.id === activeColumnId);
          const phaseColor = draggedPhase?.color || '#6b7280';
          const phaseCount = statsMap.get(activeColumnId);
          return (
            <div 
              className="w-72 rounded-lg border-2 border-primary/40 bg-card shadow-2xl"
              style={{ 
                opacity: 0.92,
                transform: 'rotate(2deg) scale(1.02)',
              }}
            >
              {/* Header realista com cor da fase */}
              <div 
                className="px-3 py-2.5 rounded-t-lg border-b border-border/50"
                style={{ backgroundColor: hexToRgba(phaseColor, 0.2) }}
              >
                <div className="flex items-center gap-2">
                  <span 
                    className="w-3 h-3 rounded-full shrink-0"
                    style={{ backgroundColor: phaseColor }}
                  />
                  <span className="font-semibold text-sm truncate">
                    {draggedPhase?.name || 'Coluna'}
                  </span>
                  <span className="text-xs text-muted-foreground bg-background/50 px-1.5 py-0.5 rounded">
                    {phaseCount ?? 0}
                  </span>
                </div>
              </div>
              {/* Corpo com placeholder de cards */}
              <div className="p-2 space-y-2">
                {[1, 2, 3].map(i => (
                  <div 
                    key={i} 
                    className="h-12 rounded-md bg-muted/40 border border-border/30"
                    style={{ opacity: 1.1 - i * 0.3 }}
                  />
                ))}
              </div>
            </div>
          );
        })()}
      </DragOverlay>

      {/* Modal de Chat */}
      <ChatConversationModal
        conversationId={selectedConversationId}
        open={isChatModalOpen}
        onOpenChange={handleCloseModal}
      />

      {/* Modal de Deal (CRM) - Abre ao clicar na seção CRM do card */}
      <DealDetailsModalPremium
        dealId={selectedDealId}
        open={isDealModalOpen}
        onOpenChange={handleCloseDealModal}
      />
    </DndContext>
  );
}
